import { useState, useMemo } from 'react';
import {
  Plus,
  Search,
  FileText,
  Truck,
  Package,
  MoreHorizontal,
  Eye,
  Edit,
  Send,
  XCircle,
  Filter,
  Download,
  Wallet,
  PenLine,
  CheckCircle2,
  Building2,
  Trash2,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PurchaseOrderDialog } from '@/components/procurement/PurchaseOrderDialog';
import { ReceiveDialog } from '@/components/procurement/ReceiveDialog';
import { SupplierDialog } from '@/components/procurement/SupplierDialog';
import { StatusCard } from '@/components/dashboard/StatusCard';
import { useInventory, PurchaseOrder, Supplier } from '@/context/InventoryContext';
import { useToast } from '@/hooks/use-toast';

const locations = [
  { id: 1, name: 'Main Warehouse' },
  { id: 2, name: 'Production Floor' },
  { id: 3, name: 'Quality Hold' },
];

const poStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }> = {
  in_progress: { label: 'In Progress', variant: 'warning' },
  submitted: { label: 'Order Received', variant: 'default' },
  approved: { label: 'Approved', variant: 'default' },
  sent: { label: 'Sent', variant: 'warning' },
  partial: { label: 'Partial', variant: 'warning' },
  received: { label: 'Received', variant: 'success' },
  closed: { label: 'Closed', variant: 'secondary' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },
};

const paymentStatusConfig: Record<string, { label: string; className: string }> = {
  unpaid: { label: 'Unpaid', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  paid: { label: 'Paid', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  partial: { label: 'Partial', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
};

export function ProcurementPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');

  // Get state from inventory context (persisted across navigation)
  const { purchaseOrders, setComponentOrders, updatePurchaseOrder, createPurchaseOrder, deletePurchaseOrder, suppliers, createSupplier, updateSupplier, deleteSupplier, products: contextProducts } = useInventory();

  // Map products from context - filter only components (not assemblies)
  const products = useMemo(() => {
    return contextProducts
      .filter(p => p.type === 'component')
      .map(p => ({
        id: p.id,
        sku: p.sku,
        name: p.name,
        manufacturer: p.manufacturer,
        unit_price: p.unitCost,
      }));
  }, [contextProducts]);
  const { toast } = useToast();

  // Dialog states
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [deleteSupplierState, setDeleteSupplierState] = useState<Supplier | null>(null);
  const [deletePOState, setDeletePOState] = useState<PurchaseOrder | null>(null);

  // Filter POs
  const filteredPOs = useMemo(() => {
    console.log('[ProcurementPage] useMemo recalculating filteredPOs - purchaseOrders length:', purchaseOrders.length);
    console.log('[ProcurementPage] purchaseOrders IDs:', purchaseOrders.map(po => po.id));
    return purchaseOrders.filter((po) => {
      const matchesSearch =
        po.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        po.supplier_name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
      const matchesSupplier = supplierFilter === 'all' || po.supplier_id === parseInt(supplierFilter);

      return matchesSearch && matchesStatus && matchesSupplier;
    });
  }, [purchaseOrders, searchQuery, statusFilter, supplierFilter]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    // Total Open Value = sum of Total from POs with payment_status = 'unpaid'
    const unpaidPOs = purchaseOrders.filter(po => (po.payment_status || 'unpaid') === 'unpaid');
    const totalOpenValue = unpaidPOs.reduce((sum, po) => sum + po.total_amount, 0);
    // Total Paid = sum of Total from POs with payment_status = 'paid'
    const paidPOs = purchaseOrders.filter(po => po.payment_status === 'paid');
    const totalPaidValue = paidPOs.reduce((sum, po) => sum + po.total_amount, 0);
    const inProgressCount = purchaseOrders.filter(po => po.status === 'in_progress').length;

    return { totalOpenValue, totalPaidValue, inProgressCount };
  }, [purchaseOrders]);

  // Calculate supplier open values from unpaid POs
  const supplierOpenValues = useMemo(() => {
    const unpaidPOs = purchaseOrders.filter(po => (po.payment_status || 'unpaid') === 'unpaid');

    // Group by supplier and sum totals
    const supplierTotals = new Map<number, { name: string; total: number; poCount: number }>();

    unpaidPOs.forEach(po => {
      const existing = supplierTotals.get(po.supplier_id);
      if (existing) {
        existing.total += po.total_amount;
        existing.poCount += 1;
      } else {
        supplierTotals.set(po.supplier_id, {
          name: po.supplier_name,
          total: po.total_amount,
          poCount: 1,
        });
      }
    });

    // Convert to array and sort by total descending
    return Array.from(supplierTotals.entries())
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.total - a.total);
  }, [purchaseOrders]);

  // Handlers
  const handleCreatePO = () => {
    setSelectedPO(null);
    setPoDialogOpen(true);
  };

  const handleEditPO = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setPoDialogOpen(true);
  };

  const handleReceivePO = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setReceiveDialogOpen(true);
  };

  const handleViewDetails = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setViewDetailsDialogOpen(true);
  };

  const handleSubmitForApproval = (po: PurchaseOrder) => {
    // Update PO status - instant save
    updatePurchaseOrder(po.id, { status: 'submitted' });

    // Update inventory - add ordered quantities
    setComponentOrders(prev => {
      const newOrders = new Map(prev);
      po.lines.forEach(line => {
        const currentQty = newOrders.get(line.product_id) || 0;
        newOrders.set(line.product_id, currentQty + line.ordered_qty);
      });
      return newOrders;
    });
  };

  const handleRevertToInProgress = (po: PurchaseOrder) => {
    // Update PO status - instant save
    updatePurchaseOrder(po.id, { status: 'in_progress' });

    // Update inventory - subtract ordered quantities
    setComponentOrders(prev => {
      const newOrders = new Map(prev);
      po.lines.forEach(line => {
        const currentQty = newOrders.get(line.product_id) || 0;
        const newQty = Math.max(0, currentQty - line.ordered_qty);
        if (newQty > 0) {
          newOrders.set(line.product_id, newQty);
        } else {
          newOrders.delete(line.product_id);
        }
      });
      return newOrders;
    });
  };

  const handleCancelPO = (po: PurchaseOrder) => {
    // If PO was already submitted, subtract from inventory
    if (po.status === 'submitted') {
      setComponentOrders(prev => {
        const newOrders = new Map(prev);
        po.lines.forEach(line => {
          const currentQty = newOrders.get(line.product_id) || 0;
          const newQty = Math.max(0, currentQty - line.ordered_qty);
          if (newQty > 0) {
            newOrders.set(line.product_id, newQty);
          } else {
            newOrders.delete(line.product_id);
          }
        });
        return newOrders;
      });
    }

    // Update PO status - instant save
    updatePurchaseOrder(po.id, { status: 'cancelled' });
  };

  const handleAddSupplier = () => {
    setSelectedSupplier(null);
    setSupplierDialogOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setSupplierDialogOpen(true);
  };

  const handleDeleteSupplier = async () => {
    if (!deleteSupplierState) return;

    const success = await deleteSupplier(deleteSupplierState.id);
    if (success) {
      toast({
        title: 'Supplier deleted',
        description: 'The supplier has been deleted successfully.',
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to delete supplier.',
        variant: 'destructive',
      });
    }
    setDeleteSupplierState(null);
  };

  const handleDeletePO = async () => {
    if (!deletePOState) return;

    const success = await deletePurchaseOrder(deletePOState.id);
    if (success) {
      toast({
        title: 'Purchase Order deleted',
        description: `PO ${deletePOState.po_number} has been deleted successfully.`,
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to delete purchase order.',
        variant: 'destructive',
      });
    }
    setDeletePOState(null);
  };

  const handlePOSubmit = async (data: any) => {
    console.log('[handlePOSubmit] Starting - purchaseOrders length BEFORE:', purchaseOrders.length);
    const supplier = suppliers.find(s => s.id === data.supplier_id);
    const totalAmount = data.lines.reduce((sum: number, line: any) => sum + line.total, 0);

    const poLines = data.lines.map((line: any, index: number) => ({
      id: index + 1,
      product_id: line.product_id,
      product_name: line.product_name,
      sku: line.sku,
      ordered_qty: line.quantity,
      received_qty: 0,
      remaining_qty: line.quantity,
    }));

    if (selectedPO) {
      // Edit existing PO - use instant save API
      await updatePurchaseOrder(selectedPO.id, {
        po_number: data.po_number,
        supplier_id: data.supplier_id,
        supplier_name: supplier?.name || '',
        total_amount: totalAmount,
        expected_delivery: data.expected_delivery,
        notes: data.notes || '',
        lines: poLines,
      });
    } else {
      // Create new PO - use instant save API (optimistic update handles UI)
      console.log('[handlePOSubmit] About to call createPurchaseOrder');
      const result = await createPurchaseOrder({
        po_number: data.po_number,
        supplier_id: data.supplier_id,
        supplier_name: supplier?.name || '',
        total_amount: totalAmount,
        status: 'in_progress',
        payment_status: 'unpaid',
        expected_delivery: data.expected_delivery,
        notes: data.notes || '',
        lines: poLines,
      });
      console.log('[handlePOSubmit] createPurchaseOrder returned:', result);
      console.log('[handlePOSubmit] purchaseOrders length AFTER:', purchaseOrders.length);
    }
    setPoDialogOpen(false);
    console.log('[handlePOSubmit] Dialog closed');
  };

  const handleReceiveSubmit = (data: any) => {
    console.log('Receive Submit:', data);
    setReceiveDialogOpen(false);
  };

  const handleSupplierSubmit = async (data: any) => {
    if (selectedSupplier) {
      // Update existing supplier
      const success = await updateSupplier(selectedSupplier.id, data);
      if (success) {
        toast({
          title: 'Supplier updated',
          description: 'The supplier has been updated successfully.',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update supplier.',
          variant: 'destructive',
        });
      }
    } else {
      // Create new supplier
      const result = await createSupplier(data);
      if (result.success) {
        toast({
          title: 'Supplier created',
          description: 'The new supplier has been added successfully.',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create supplier.',
          variant: 'destructive',
        });
      }
    }
    setSupplierDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Procurement</h2>
          <p className="text-muted-foreground">
            Manage purchase orders and supplier relationships
          </p>
        </div>
        <Button onClick={handleCreatePO}>
          <Plus className="h-4 w-4 mr-2" />
          Create PO
        </Button>
      </div>

      {/* Summary Cards - Modern Floating Design */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
        <StatusCard
          title="Total Open Value"
          value={formatCurrency(summaryStats.totalOpenValue)}
          subtext={summaryStats.totalOpenValue > 0 ? 'Unpaid' : 'All paid'}
          trend={summaryStats.totalOpenValue > 0 ? 'down' : 'up'}
          type="custom"
          icon={Wallet}
          iconColor="text-red-500"
          iconBgColor="bg-red-500/10"
          gradientColor="red"
        />

        <StatusCard
          title="Total Paid"
          value={formatCurrency(summaryStats.totalPaidValue)}
          subtext={summaryStats.totalPaidValue > 0 ? 'Completed' : 'No payments'}
          trend={summaryStats.totalPaidValue > 0 ? 'up' : 'neutral'}
          type="custom"
          icon={CheckCircle2}
          iconColor="text-green-500"
          iconBgColor="bg-green-500/10"
          gradientColor="green"
        />

        <StatusCard
          title="In Progress"
          value={summaryStats.inProgressCount.toString()}
          subtext={summaryStats.inProgressCount > 0 ? 'Drafts' : 'All submitted'}
          trend={summaryStats.inProgressCount > 0 ? 'neutral' : 'up'}
          type="custom"
          icon={PenLine}
          iconColor="text-purple-500"
          iconBgColor="bg-purple-500/10"
          gradientColor="purple"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="orders" className="space-y-4">
        <TabsList>
          <TabsTrigger value="orders" className="gap-2">
            <FileText className="h-4 w-4" />
            Purchase Orders
          </TabsTrigger>
          <TabsTrigger value="supplier-open-value" className="gap-2">
            <Building2 className="h-4 w-4" />
            Supplier Open Value
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="gap-2">
            <Truck className="h-4 w-4" />
            Suppliers
          </TabsTrigger>
        </TabsList>

        {/* Purchase Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search POs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {Object.entries(poStatusConfig).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id.toString()}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* PO Table */}
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">PO Number</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Total</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Payment</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Expected</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Created</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Notes</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPOs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                        No purchase orders found
                      </td>
                    </tr>
                  ) : (
                    filteredPOs.map((po) => {
                      const statusConfig = poStatusConfig[po.status] || poStatusConfig.draft;

                      return (
                        <tr key={po.id} className="border-b hover:bg-muted/30">
                          <td className="px-4 py-3 text-sm">
                            <span className="font-mono text-primary font-medium">
                              {po.po_number}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">{po.supplier_name}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium">
                            {formatCurrency(po.total_amount)}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <Select
                              value={po.payment_status || 'unpaid'}
                              onValueChange={(value: 'unpaid' | 'paid' | 'partial') => {
                                // Instant save
                                updatePurchaseOrder(po.id, { payment_status: value });
                              }}
                            >
                              <SelectTrigger className={`w-[100px] h-7 text-xs font-medium border-0 ${paymentStatusConfig[po.payment_status || 'unpaid']?.className || paymentStatusConfig.unpaid.className}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unpaid">
                                  <span className="flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-red-500" />
                                    Unpaid
                                  </span>
                                </SelectItem>
                                <SelectItem value="paid">
                                  <span className="flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-green-500" />
                                    Paid
                                  </span>
                                </SelectItem>
                                <SelectItem value="partial">
                                  <span className="flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                                    Partial
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">{po.expected_delivery}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {po.created_at ? new Date(po.created_at).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            }) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                            {po.notes || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewDetails(po)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {po.status === 'in_progress' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleEditPO(po)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleSubmitForApproval(po)}>
                                      <Send className="h-4 w-4 mr-2" />
                                      Mark as Order Received
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {po.status === 'submitted' && (
                                  <DropdownMenuItem onClick={() => handleRevertToInProgress(po)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Revert to In Progress
                                  </DropdownMenuItem>
                                )}
                                {po.status === 'approved' && (
                                  <DropdownMenuItem>
                                    <Send className="h-4 w-4 mr-2" />
                                    Send to Supplier
                                  </DropdownMenuItem>
                                )}
                                {['sent', 'partial'].includes(po.status) && (
                                  <DropdownMenuItem onClick={() => handleReceivePO(po)}>
                                    <Package className="h-4 w-4 mr-2" />
                                    Receive Items
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {!['received', 'closed', 'cancelled'].includes(po.status) && (
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => handleCancelPO(po)}
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Cancel PO
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => setDeletePOState(po)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete PO
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Supplier Open Value Tab */}
        <TabsContent value="supplier-open-value" className="space-y-4">
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Unpaid POs</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Open Value</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierOpenValues.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                        No unpaid purchase orders
                      </td>
                    </tr>
                  ) : (
                    <>
                      {supplierOpenValues.map((supplier) => (
                        <tr key={supplier.id} className="border-b hover:bg-muted/30">
                          <td className="px-4 py-3 text-sm">
                            <p className="font-medium">{supplier.name}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <Badge variant="secondary">{supplier.poCount}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-red-600 dark:text-red-400">
                            {formatCurrency(supplier.total)}
                          </td>
                        </tr>
                      ))}
                      {/* Total row */}
                      <tr className="bg-muted/50 font-semibold">
                        <td className="px-4 py-3 text-sm">Total</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <Badge variant="default">
                            {supplierOpenValues.reduce((sum, s) => sum + s.poCount, 0)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">
                          {formatCurrency(supplierOpenValues.reduce((sum, s) => sum + s.total, 0))}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-4">
          {/* Suppliers Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search suppliers..." className="pl-10" />
            </div>
            <Button onClick={handleAddSupplier}>
              <Plus className="h-4 w-4 mr-2" />
              Add Supplier
            </Button>
          </div>

          {/* Suppliers Table */}
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Code</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Terms</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Lead Time</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((supplier) => (
                    <tr key={supplier.id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">
                        <p className="font-medium">{supplier.name}</p>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-muted-foreground">
                        {supplier.code}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <p>{supplier.contact_name}</p>
                        <p className="text-muted-foreground text-xs">{supplier.contact_email}</p>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant="secondary">{supplier.payment_terms}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {supplier.lead_time_days} days
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditSupplier(supplier)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleCreatePO}>
                              <Plus className="h-4 w-4 mr-2" />
                              Create PO
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteSupplierState(supplier)}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <PurchaseOrderDialog
        open={poDialogOpen}
        onOpenChange={setPoDialogOpen}
        suppliers={suppliers.map(s => ({ id: s.id, name: s.name, payment_terms: s.payment_terms }))}
        products={products}
        existingPO={selectedPO ? {
          id: selectedPO.id,
          po_number: selectedPO.po_number,
          supplier_id: selectedPO.supplier_id,
          expected_delivery: selectedPO.expected_delivery,
          notes: selectedPO.notes,
          lines: selectedPO.lines.map(l => ({
            product_id: l.product_id,
            product_name: l.product_name,
            sku: l.sku,
            quantity: l.ordered_qty,
            unit_price: products.find(p => p.id === l.product_id)?.unit_price || 0,
            total: l.ordered_qty * (products.find(p => p.id === l.product_id)?.unit_price || 0),
          })),
        } : undefined}
        onSubmit={handlePOSubmit}
      />

      <ReceiveDialog
        open={receiveDialogOpen}
        onOpenChange={setReceiveDialogOpen}
        poNumber={selectedPO?.po_number || ''}
        lines={selectedPO?.lines || []}
        locations={locations}
        onSubmit={handleReceiveSubmit}
      />

      <SupplierDialog
        open={supplierDialogOpen}
        onOpenChange={setSupplierDialogOpen}
        existingSupplier={selectedSupplier || undefined}
        onSubmit={handleSupplierSubmit}
      />

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialogOpen} onOpenChange={setViewDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>PO Details - {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-medium">{selectedPO.supplier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={poStatusConfig[selectedPO.status]?.variant || 'secondary'}>
                    {poStatusConfig[selectedPO.status]?.label || selectedPO.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expected Delivery</p>
                  <p className="font-medium">{selectedPO.expected_delivery}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{selectedPO.created_at ? new Date(selectedPO.created_at).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }) : '-'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">Line Items</p>
                <div className="rounded-lg border">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left text-sm font-medium">Product</th>
                        <th className="px-3 py-2 text-right text-sm font-medium">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPO.lines.map((line) => (
                        <tr key={line.id} className="border-b last:border-0">
                          <td className="px-3 py-2 text-sm">
                            <p className="font-medium">{line.product_name}</p>
                            <p className="text-xs text-muted-foreground">{line.sku}</p>
                          </td>
                          <td className="px-3 py-2 text-sm text-right">{line.ordered_qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedPO.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm bg-muted/50 p-2 rounded">{selectedPO.notes}</p>
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-semibold">Total</span>
                <span className="text-lg font-bold text-primary">
                  {formatCurrency(selectedPO.total_amount)}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Supplier Confirmation */}
      <AlertDialog open={!!deleteSupplierState} onOpenChange={() => setDeleteSupplierState(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteSupplierState?.name}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSupplier}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete PO Confirmation */}
      <AlertDialog open={!!deletePOState} onOpenChange={() => setDeletePOState(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletePOState?.po_number}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePO}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
